import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiVideo,
  FiSearch,
  FiPlus,
  FiRefreshCcw,
  FiFilter,
  FiCalendar,
  FiBook,
  FiLayers,
  FiPackage,
} from "react-icons/fi";
import Button from "../../components/Button/Button";
import adminApi from "../../api/adminApi";
import Table from "../../components/Table/Table";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import Select from "../../components/Select/Select";
import "./Lectures.css";

const Lectures = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [modules, setModules] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState("table"); // "table" or "calendar"

  useEffect(() => {
    loadCourses();
    loadBatches();
  }, []);

  // Load modules and lectures when batches are loaded
  useEffect(() => {
    if (batches.length > 0) {
      loadModules();
      loadLectures();
    }
  }, [batches.length]);

  // Reload lectures when filters or page change
  useEffect(() => {
    if (batches.length > 0) {
      loadLectures();
    }
  }, [page, courseFilter, batchFilter, moduleFilter, statusFilter, dateFilter]);

  const loadCourses = async () => {
    try {
      const response = await adminApi.get("/courses", {
        params: { limit: 1000 },
      });
      const coursesList = response.data?.courses || response.data?.data || [];
      setCourses(coursesList);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await adminApi.get("/batches", {
        params: { limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(batchesList);
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const loadModules = async () => {
    try {
      const batchesToFetch =
        batchFilter !== "all"
          ? batches.filter((b) => b._id === batchFilter)
          : courseFilter !== "all"
          ? batches.filter(
              (b) => (b.course?._id || b.course) === courseFilter
            )
          : batches;

      const allModules = [];
      for (const batch of batchesToFetch) {
        try {
          const response = await adminApi.get(
            `/batches/${batch._id}/modules`
          );
          const modulesList =
            response.data?.modules ||
            response.data?.data ||
            response.data ||
            [];
          allModules.push(...modulesList);
        } catch (err) {
          console.error(`Failed to load modules for batch ${batch._id}:`, err);
        }
      }
      setModules(allModules);
    } catch (err) {
      console.error("Failed to load modules:", err);
    }
  };

  const loadLectures = async () => {
    setLoading(true);
    setError(null);
    try {
      const allLectures = [];
      const batchesToFetch =
        batchFilter !== "all"
          ? batches.filter((b) => b._id === batchFilter)
          : courseFilter !== "all"
          ? batches.filter(
              (b) => (b.course?._id || b.course) === courseFilter
            )
          : batches;

      for (const batch of batchesToFetch) {
        try {
          const params = {
            page: 1,
            limit: 1000,
          };
          if (statusFilter !== "all") {
            params.status = statusFilter;
          }
          const response = await adminApi.get(
            `/batches/${batch._id}/lectures`,
            { params }
          );
          const lecturesList =
            response.data?.lectures ||
            response.data?.data ||
            response.data ||
            [];
          // Enrich lectures with batch, course, and module info
          const enrichedLectures = lecturesList.map((lecture) => ({
            ...lecture,
            batch: batch,
            batchName: batch.batchName,
            course: batch.course,
            courseTitle:
              batch.course &&
              typeof batch.course === "object" &&
              batch.course.title
                ? batch.course.title
                : "Unknown Course",
          }));
          allLectures.push(...enrichedLectures);
        } catch (err) {
          console.error(`Failed to load lectures for batch ${batch._id}:`, err);
        }
      }

      // Apply module filter
      let filteredLectures = allLectures;
      if (moduleFilter !== "all") {
        filteredLectures = allLectures.filter(
          (l) =>
            (l.module?._id || l.module) === moduleFilter ||
            (typeof l.module === "object" && l.module?._id === moduleFilter)
        );
      }

      // Apply date filter
      if (dateFilter !== "all") {
        const now = new Date();
        filteredLectures = filteredLectures.filter((l) => {
          if (!l.startTime) return false;
          const startDate = new Date(l.startTime);
          switch (dateFilter) {
            case "today":
              return (
                startDate.toDateString() === now.toDateString()
              );
            case "thisWeek":
              const weekAgo = new Date(now);
              weekAgo.setDate(weekAgo.getDate() - 7);
              return startDate >= weekAgo && startDate <= now;
            case "upcoming":
              return startDate > now;
            case "past":
              return startDate < now;
            default:
              return true;
          }
        });
      }

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredLectures = filteredLectures.filter(
          (lecture) =>
            lecture.topic?.toLowerCase().includes(query) ||
            lecture.description?.toLowerCase().includes(query) ||
            lecture.batchName?.toLowerCase().includes(query) ||
            lecture.courseTitle?.toLowerCase().includes(query) ||
            (lecture.module &&
              typeof lecture.module === "object" &&
              lecture.module.title?.toLowerCase().includes(query))
        );
      }

      // Sort by start time
      filteredLectures.sort(
        (a, b) => new Date(a.startTime) - new Date(b.startTime)
      );

      setLectures(filteredLectures);
      setTotalPages(Math.ceil(filteredLectures.length / 25));
    } catch (err) {
      console.error("Failed to load lectures:", err);
      setError(err.response?.data?.message || "Failed to load lectures");
    } finally {
      setLoading(false);
    }
  };

  const filteredLectures = useMemo(() => {
    const startIndex = (page - 1) * 25;
    return lectures.slice(startIndex, startIndex + 25);
  }, [lectures, page]);

  const getStatusBadge = (lecture) => {
    if (!lecture.startTime) return "scheduled";
    const now = new Date();
    const start = new Date(lecture.startTime);
    const end = lecture.endTime ? new Date(lecture.endTime) : null;

    if (lecture.status === "cancelled") return "cancelled";
    if (lecture.status === "completed") return "completed";
    if (end && now > end) return "completed";
    if (start <= now && (!end || now <= end)) return "active";
    return "upcoming";
  };

  const columns = [
    {
      key: "topic",
      label: "Topic",
      render: (row) => (
        <Link to={`/lectures/${row._id}`} className="lecture-link">
          <strong>{row.topic || "Untitled"}</strong>
        </Link>
      ),
    },
    {
      key: "course",
      label: "Course",
      render: (row) => {
        if (!row.course) return "—";
        const courseId = typeof row.course === "object" ? row.course._id : row.course;
        return (
          <Link to={`/courses/${courseId}`} className="course-link">
            {row.courseTitle || "Unknown Course"}
          </Link>
        );
      },
    },
    {
      key: "batch",
      label: "Batch",
      render: (row) => (
        <Link
          to={`/batches/${row.batch?._id || row.batch}`}
          className="batch-link"
        >
          {row.batchName || "Unknown Batch"}
        </Link>
      ),
    },
    {
      key: "module",
      label: "Module",
      render: (row) => {
        if (!row.module) return "—";
        const moduleTitle =
          typeof row.module === "object" ? row.module.title : "Module";
        return (
          <Link
            to={`/modules/${
              typeof row.module === "object" ? row.module._id : row.module
            }`}
            className="module-link"
          >
            {moduleTitle}
          </Link>
        );
      },
    },
    {
      key: "teacher",
      label: "Teacher",
      render: (row) => {
        if (!row.teacher) return "—";
        if (typeof row.teacher === "object") {
          const firstName =
            row.teacher.user?.firstName || row.teacher.firstName || "";
          const lastName =
            row.teacher.user?.lastName || row.teacher.lastName || "";
          return `${firstName} ${lastName}`.trim() || "Unknown";
        }
        return "—";
      },
    },
    {
      key: "startTime",
      label: "Start Time",
      render: (row) =>
        row.startTime
          ? new Date(row.startTime).toLocaleString()
          : "—",
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const status = getStatusBadge(row);
        return <StatusBadge status={status} />;
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="action-buttons">
          <Button
            size="small"
            variant="secondary"
            onClick={() => navigate(`/lectures/${row._id}`)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="lectures-page">
      <div className="page-header">
        <div>
          <h1>
            <FiVideo size={24} />
            Lectures
          </h1>
          <p>Manage and schedule all lectures</p>
        </div>
        <div className="header-actions-group">
          <div className="view-toggle">
            <Button
              variant={viewMode === "table" ? "primary" : "secondary"}
              size="small"
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              variant={viewMode === "calendar" ? "primary" : "secondary"}
              size="small"
              onClick={() => setViewMode("calendar")}
            >
              <FiCalendar size={16} />
              Calendar
            </Button>
          </div>
          <Button onClick={() => navigate("/lectures/new")}>
            <FiPlus size={18} />
            Schedule Lecture
          </Button>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-bar">
          <FiSearch size={18} />
          <input
            type="text"
            placeholder="Search lectures by topic, description, course, batch..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <Select
          label="Course"
          value={courseFilter}
          onChange={(e) => {
            setCourseFilter(e.target.value);
            setBatchFilter("all");
            setModuleFilter("all");
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Courses" },
            ...courses.map((c) => ({ value: c._id, label: c.title })),
          ]}
        />
        <Select
          label="Batch"
          value={batchFilter}
          onChange={(e) => {
            setBatchFilter(e.target.value);
            setModuleFilter("all");
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Batches" },
            ...(courseFilter !== "all"
              ? batches
                  .filter(
                    (b) => (b.course?._id || b.course) === courseFilter
                  )
                  .map((b) => ({
                    value: b._id,
                    label: `${b.batchName} (${
                      b.course &&
                      typeof b.course === "object" &&
                      b.course.title
                        ? b.course.title
                        : "Course"
                    })`,
                  }))
              : batches.map((b) => ({
                  value: b._id,
                  label: `${b.batchName} (${
                    b.course &&
                    typeof b.course === "object" &&
                    b.course.title
                      ? b.course.title
                      : "Course"
                  })`,
                }))),
          ]}
        />
        <Select
          label="Module"
          value={moduleFilter}
          onChange={(e) => {
            setModuleFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Modules" },
            ...(batchFilter !== "all"
              ? modules
                  .filter((m) => (m.batch?._id || m.batch) === batchFilter)
                  .map((m) => ({
                    value: m._id,
                    label: `${m.order}. ${m.title}`,
                  }))
              : modules.map((m) => ({
                  value: m._id,
                  label: `${m.order}. ${m.title} (${m.batch?.batchName || "Batch"})`,
                }))),
          ]}
        />
        <Select
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Status" },
            { value: "scheduled", label: "Scheduled" },
            { value: "live", label: "Live" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
          ]}
        />
        <Select
          label="Date"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: "all", label: "All Dates" },
            { value: "today", label: "Today" },
            { value: "thisWeek", label: "This Week" },
            { value: "upcoming", label: "Upcoming" },
            { value: "past", label: "Past" },
          ]}
        />
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            setSearchQuery("");
            setCourseFilter("all");
            setBatchFilter("all");
            setModuleFilter("all");
            setStatusFilter("all");
            setDateFilter("all");
            setPage(1);
            loadLectures();
          }}
        >
          <FiRefreshCcw size={16} />
          Reset
        </Button>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <p>Loading lectures...</p>
        </div>
      ) : filteredLectures.length > 0 ? (
        <>
          {viewMode === "table" ? (
            <>
              <Table columns={columns} data={filteredLectures} />
              {totalPages > 1 && (
                <div className="pagination">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="calendar-view">
              {Object.entries(
                filteredLectures.reduce((acc, lecture) => {
                  if (!lecture.startTime) return acc;
                  const date = new Date(lecture.startTime).toDateString();
                  if (!acc[date]) acc[date] = [];
                  acc[date].push(lecture);
                  return acc;
                }, {})
              )
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([date, dayLectures]) => (
                  <div key={date} className="calendar-day">
                    <div className="calendar-day-header">
                      <h3>{date}</h3>
                      <span className="lecture-count">
                        {dayLectures.length} lecture{dayLectures.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="calendar-lectures">
                      {dayLectures
                        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
                        .map((lecture) => (
                          <div
                            key={lecture._id}
                            className="calendar-lecture-item"
                            onClick={() => navigate(`/lectures/${lecture._id}`)}
                          >
                            <div className="lecture-time">
                              {new Date(lecture.startTime).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              {" - "}
                              {new Date(lecture.endTime).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                            <div className="lecture-topic">{lecture.topic}</div>
                            <div className="lecture-meta">
                              {lecture.batchName && (
                                <span className="meta-item">{lecture.batchName}</span>
                              )}
                              {lecture.module &&
                                typeof lecture.module === "object" &&
                                lecture.module.title && (
                                  <span className="meta-item">
                                    {lecture.module.title}
                                  </span>
                                )}
                              <StatusBadge status={getStatusBadge(lecture)} />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <FiVideo size={48} />
          <h3>No lectures found</h3>
          <p>
            {searchQuery ||
            courseFilter !== "all" ||
            batchFilter !== "all" ||
            moduleFilter !== "all" ||
            statusFilter !== "all" ||
            dateFilter !== "all"
              ? "Try adjusting your filters"
              : "Schedule your first lecture to get started"}
          </p>
          {!searchQuery &&
            courseFilter === "all" &&
            batchFilter === "all" &&
            moduleFilter === "all" &&
            statusFilter === "all" &&
            dateFilter === "all" && (
              <Button onClick={() => navigate("/lectures/new")}>
                <FiPlus size={18} />
                Schedule Lecture
              </Button>
            )}
        </div>
      )}
    </div>
  );
};

export default Lectures;

